import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { IconPlus, IconTrash, IconVariable, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

const VARIABLE_NAME_REGEX = /^[A-Z][A-Z0-9_]{0,63}$/;

const TYPE_OPTIONS = [
  { value: 'boolean', label: 'Verdadeiro/Falso' },
  { value: 'string', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'enum', label: 'Opções (enum)' },
];

const isValidName = (name) => VARIABLE_NAME_REGEX.test(name || '');

/**
 * ExtractVariablesEditor — lista editável de variáveis que a IA deve extrair.
 *
 * Cada variável tem {name, type, description, options}. options só aparece
 * quando type === 'enum'.
 *
 * O nome obedece UPPER_SNAKE_CASE (regex ^[A-Z][A-Z0-9_]{0,63}$). Erros são
 * mostrados inline; salvar com nome inválido é responsabilidade do caller —
 * o backend descarta nomes inválidos com log warning.
 */
const ExtractVariablesEditor = ({ variables = [], onChange }) => {
  const [open, setOpen] = useState((variables || []).length > 0);

  const update = (index, field, value) => {
    const next = [...variables];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addVariable = () => {
    onChange([...variables, { name: '', type: 'boolean', description: '', options: [] }]);
    setOpen(true);
  };

  const removeVariable = (index) => {
    const next = variables.filter((_, i) => i !== index);
    onChange(next);
  };

  const setOptions = (index, rawText) => {
    // Lista separada por vírgula ou nova linha; preserva ordem e remove vazias
    const opts = rawText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    update(index, 'options', opts);
  };

  return (
    <Form.Group className="mb-3">
      <div
        className="d-flex justify-content-between align-items-center mb-2"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen((o) => !o)}
      >
        <Form.Label className="afc-form-label mb-0">
          {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <IconVariable size={14} className="ms-1 me-1" />
          Variáveis a Extrair {variables.length > 0 && <small className="text-muted">({variables.length})</small>}
        </Form.Label>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            addVariable();
          }}
        >
          <IconPlus size={13} className="me-1" />
          Adicionar
        </Button>
      </div>

      {open && (
        <>
          {variables.length === 0 && (
            <Form.Text className="afc-form-hint d-block mb-2">
              Declare variáveis para a IA extrair da mensagem do cliente. Ex: <code>COMPROVANTE_VALIDO</code>{' '}
              (boolean) para verificar mais tarde em um nó de Dados.
            </Form.Text>
          )}

          {variables.map((variable, index) => {
            const nameError =
              (variable.name || '').length > 0 && !isValidName(variable.name)
                ? 'Use UPPER_SNAKE_CASE (ex: COMPROVANTE_VALIDO, VALOR_PAGO)'
                : null;

            return (
              <div
                key={index}
                className="afc-extract-var-row mb-3 p-2"
                style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fafafa' }}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <small className="text-muted">Variável #{index + 1}</small>
                  <Button
                    size="sm"
                    variant="link"
                    className="text-danger p-0"
                    onClick={() => removeVariable(index)}
                    title="Remover"
                  >
                    <IconTrash size={14} />
                  </Button>
                </div>

                <Form.Group className="mb-2">
                  <Form.Label className="afc-form-label small mb-1">Nome</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    className="afc-form-control"
                    value={variable.name || ''}
                    onChange={(e) => update(index, 'name', e.target.value.toUpperCase())}
                    placeholder="COMPROVANTE_VALIDO"
                    isInvalid={!!nameError}
                  />
                  {nameError && (
                    <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="afc-form-label small mb-1">Tipo</Form.Label>
                  <Form.Select
                    size="sm"
                    className="afc-form-control"
                    value={variable.type || 'boolean'}
                    onChange={(e) => update(index, 'type', e.target.value)}
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="afc-form-label small mb-1">Descrição (para a IA)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    className="afc-form-control"
                    value={variable.description || ''}
                    onChange={(e) => update(index, 'description', e.target.value)}
                    placeholder="Ex: True se o cliente enviou comprovante de pagamento legível."
                  />
                </Form.Group>

                {variable.type === 'enum' && (
                  <Form.Group className="mb-2">
                    <Form.Label className="afc-form-label small mb-1">
                      Opções (uma por linha ou separadas por vírgula)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      className="afc-form-control"
                      value={(variable.options || []).join('\n')}
                      onChange={(e) => setOptions(index, e.target.value)}
                      placeholder="aprovado&#10;reprovado&#10;pendente"
                    />
                    {(variable.options || []).length === 0 && (
                      <Form.Text className="text-warning">
                        Sem opções declaradas — tipo será tratado como texto livre.
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              </div>
            );
          })}

          {variables.length > 0 && (
            <Alert variant="light" className="afc-form-hint small mb-0">
              Quando a IA não conseguir inferir o valor com confiança, a variável fica vazia (não setada) —
              comportamento ideal para usar com o nó <strong>Dados</strong> e o operador{' '}
              <code>existe</code>.
            </Alert>
          )}
        </>
      )}
    </Form.Group>
  );
};

export default ExtractVariablesEditor;
export { VARIABLE_NAME_REGEX, isValidName };
